import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Typography,
  makeStyles,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Icon } from "@opendash/icons";
import { Checkbox, Form } from "antd";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useApp } from "../context";
import { AudioDonation } from "../parse";
import { UploadCard } from "./donation/UploadCard";

interface Audio {
  url: string;
  name: string;
  audioBlob: Blob | File;
  metadata?: AudioMetadata;
}

export interface AudioMetadata {
  synthetic: boolean;
  voiceGender: "male" | "female" | "diverse" | "";
  speakerAge: number | "";
  spokenText: string;
  language: string;
  microphoneType:
    | "laptop"
    | "phone"
    | "wireless_headphone"
    | "professional"
    | "";
  recordingEnvironment: "quiet" | "moderate_noise" | "noisy" | "";
  confirmedRights: boolean;
  confirmedDataset: boolean;
  confirmedGame: boolean;
}

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    maxWidth: 800,
    margin: "0 auto",
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  recordingSection: {
    textAlign: "center",
    padding: theme.spacing(3),
  },
  uploadSection: {
    textAlign: "center",
    padding: theme.spacing(3),
  },
  recordButton: {
    width: 120,
    height: 120,
    borderRadius: "50%",
    fontSize: "3rem",
    margin: theme.spacing(2),
  },
  audioPreview: {
    width: "100%",
    marginTop: theme.spacing(2),
  },
  hiddenInput: {
    display: "none",
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  permissionMessage: {
    marginBottom: theme.spacing(2),
  },
  audioControls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  statusText: {
    marginTop: theme.spacing(1),
  },
  metadataForm: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  formRow: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  formField: {
    flex: 1,
    minWidth: 200,
  },
}));

export const DataDonationRoute = observer(function DataDonationRoute() {
  const app = useApp();
  const classes = useStyles();
  const { t } = useTranslation();
  const [hasConfirmedRights, setHasConfirmedRights] = useState(false);

  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState<Blob | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [permissionGranted, setPermissionGranted] = useState<boolean | null>(
    null,
  );
  const [wantsToRecord, setWantsToRecord] = useState(false);

  // Upload states
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [audios, setAudios] = useState<{ [key: string]: Audio }>({});

  // Refs
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check for microphone permission and support
  useEffect(() => {
    checkMicrophoneSupport();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const createDateFileName = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    return `${year}_${month}_${day}-${hours}-${minutes}-${seconds}`;
  };

  const checkMicrophoneSupport = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setPermissionGranted(false);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setPermissionGranted(true);
      // Stop the stream immediately as we just wanted to check permission
      stream.getTracks().forEach((track) => track.stop());
    } catch (error) {
      console.error("Microphone permission denied:", error);
      setPermissionGranted(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/wav",
        });
        const url = URL.createObjectURL(audioBlob);

        // Add the recorded audio to existing audios instead of replacing them
        setAudios((prevAudios) => ({
          ...prevAudios,
          ["recording-" + createDateFileName()]: {
            url,
            audioBlob,
            name: "recording-" + createDateFileName() + ".wav",
          },
        }));

        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      console.error("Error starting recording:", error);
      setPermissionGranted(false);
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setRecordedAudio(null); // Clear recorded audio state (but keep existing audios)

      // Add the uploaded file to existing audios instead of replacing them
      setAudios((prevAudios) => ({
        ...prevAudios,
        ["file-" + createDateFileName()]: {
          url,
          audioBlob: file,
          name: file.name,
        },
      }));
    }
  };

  const handleUploadAudio = async (
    audioKey: string,
    audio: Audio,
    metadata: AudioMetadata,
  ): Promise<boolean> => {
    try {
      const AudioDonationClass = Parse.Object.extend(AudioDonation.className);
      const audioDonation = new AudioDonationClass();

      const parseFile = new Parse.File(audio.name, audio.audioBlob);

      audioDonation.set("audio", parseFile);
      audioDonation.set("synthetic", metadata.synthetic ?? false);
      audioDonation.set("voiceGender", metadata.voiceGender);
      audioDonation.set(
        "speakerAge",
        !!metadata.speakerAge ? metadata.speakerAge : undefined,
      );
      audioDonation.set("spokenText", metadata.spokenText);
      audioDonation.set("language", metadata.language);
      audioDonation.set("microphoneType", metadata.microphoneType);
      audioDonation.set("recordingEnvironment", metadata.recordingEnvironment);
      audioDonation.set("confirmedRights", true);
      audioDonation.set("confirmedDataset", true);
      audioDonation.set("confirmedGame", true);

      await audioDonation.save();

      // Remove the uploaded audio from UI to prevent duplicate uploads
      handleDeleteAudio(audioKey);

      return true;
    } catch (error) {
      console.error("Error uploading audio donation:", error);
      return false;
    }
  };

  const handleDeleteAudio = (audioKey: string) => {
    const audioToDelete = audios[audioKey];
    if (audioToDelete) {
      URL.revokeObjectURL(audioToDelete.url);
    }
    const newAudios = { ...audios };
    delete newAudios[audioKey];
    setAudios(newAudios);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const hasAudio = Object.keys(audios).length > 0;

  if (!hasConfirmedRights)
    return (
      <Card style={{ padding: "1rem" }}>
        <p>Bitte bestätigen Sie die Einwilligungen, um fortzufahren.</p>
        <Form
          layout="vertical"
          onFinish={(values: Record<string, boolean>) =>
            setHasConfirmedRights(Object.values(values).every((value) => value))
          }
        >
          <Form.Item
            name="privacyConsent"
            valuePropName="checked"
            rules={[
              {
                required: true,
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        "Bitte bestätigen Sie die Datenschutzeinwilligung.",
                      ),
              },
            ]}
          >
            <Checkbox>
              Ich willige ein, dass meine Sprachaufnahme zum Zweck des
              Anti-Scam-Projekts verarbeitet und gespeichert wird. Die
              Einzelheiten ergeben sich aus der{" "}
              <Link to="/privacy-policy-data-donation">
                Datenschutzerklärung
              </Link>
              .
            </Checkbox>
          </Form.Item>
          <Form.Item
            name="publicConsent"
            valuePropName="checked"
            rules={[
              {
                required: true,
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        "Bitte bestätigen Sie die öffentliche Einwilligung.",
                      ),
              },
            ]}
          >
            <Checkbox>
              Ich bin damit einverstanden, dass meine Sprachaufnahme im Rahmen
              des Spiels ohne weitere Angaben zu meiner Person öffentlich
              wiedergegeben wird.
            </Checkbox>
          </Form.Item>
          <Form.Item
            name="voiceConsent"
            valuePropName="checked"
            rules={[
              {
                required: true,
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        "Bitte bestätigen Sie die Einwilligung zur Verwendung Ihrer eigenen Stimme.",
                      ),
              },
            ]}
          >
            <Checkbox>
              Ich versichere, dass es sich bei der hochgeladenen Sprachaufnahme
              um meine eigene Stimme handelt.
            </Checkbox>
          </Form.Item>
          <Form.Item
            name="ageConsent"
            valuePropName="checked"
            rules={[
              {
                required: true,
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        "Bitte bestätigen Sie, dass Sie mindestens 16 Jahre alt sind.",
                      ),
              },
            ]}
          >
            <Checkbox>
              Ich bestätige, dass ich mindestens 16 Jahre alt bin.
            </Checkbox>
          </Form.Item>
          <Form.Item>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
            >
              Bestätigen
            </Button>
          </Form.Item>
        </Form>
      </Card>
    );

  return (
    <Card className={classes.container}>
      <Typography variant="h4" gutterBottom align="center">
        {t("data-donation-title")}
      </Typography>

      <Typography
        variant="body1"
        align="center"
        color="textSecondary"
        paragraph
      >
        {t("data-donation-description")}
      </Typography>

      {uploadSuccess && (
        <Alert severity="success" className={classes.permissionMessage}>
          {t("data-donation-success")}
        </Alert>
      )}

      {uploadError && (
        <Alert severity="error" className={classes.permissionMessage}>
          {uploadError}
        </Alert>
      )}

      {/* Recording Section */}
      <Card className={classes.card}>
        <CardContent className={classes.recordingSection}>
          <Typography variant="h6" gutterBottom>
            {t("record-audio")}
          </Typography>

          {!wantsToRecord && (
            <>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setWantsToRecord(true)}
                style={{ marginBottom: "1rem" }}
              >
                {t("enable-microphone-access")}
              </Button>
              {!permissionGranted && (
                <Alert severity="info">
                  {t("data-donation-recording-info")}
                </Alert>
              )}
            </>
          )}

          {wantsToRecord && (
            <>
              {permissionGranted === false && wantsToRecord && (
                <Alert severity="warning" className={classes.permissionMessage}>
                  {t("microphone-permission-denied")}
                </Alert>
              )}

              {permissionGranted === true && wantsToRecord && (
                <>
                  <Button
                    variant="contained"
                    color={isRecording ? "secondary" : "primary"}
                    className={classes.recordButton}
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isUploading}
                  >
                    {isRecording ? (
                      <Icon icon="fa:stop" />
                    ) : (
                      <Icon icon="fa:microphone" />
                    )}
                  </Button>

                  <Typography variant="body2" className={classes.statusText}>
                    {isRecording
                      ? `${t("recording-status-active")} ${formatTime(
                          recordingTime,
                        )}`
                      : t("recording-status-ready")}
                  </Typography>
                </>
              )}
            </>
          )}

          {permissionGranted === null && wantsToRecord && <CircularProgress />}
        </CardContent>
      </Card>

      <Box className={classes.divider}>
        <Divider />
        <Box
          display="flex"
          justifyContent="center"
          marginTop={1}
          marginBottom={1}
        >
          <Typography variant="body2" color="textSecondary">
            {t("or")}
          </Typography>
        </Box>
        <Divider />
      </Box>

      {/* Upload Section */}
      <Card className={classes.card}>
        <CardContent className={classes.uploadSection}>
          <Typography variant="h6" gutterBottom>
            {t("upload-audio-file")}
          </Typography>

          <input
            accept="audio/*"
            className={classes.hiddenInput}
            id="audio-file-upload"
            type="file"
            onChange={handleFileUpload}
            disabled={isUploading}
          />

          <label htmlFor="audio-file-upload">
            <Button variant="outlined" component="span" disabled={isUploading}>
              <Icon icon="fa:upload" style={{ marginRight: 10 }} />
              {t("select-audio-file")}
            </Button>
          </label>

          {uploadedFile && (
            <Typography variant="body2" className={classes.statusText}>
              {t("selected-file")} {uploadedFile.name}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Audio Upload Cards */}
      {hasAudio &&
        Object.keys(audios).map((audioKey) => (
          <UploadCard
            key={audioKey}
            audioKey={audioKey}
            audio={audios[audioKey]}
            onDelete={handleDeleteAudio}
            onUpload={handleUploadAudio}
          />
        ))}

      {/* Clear All Button */}
      {hasAudio && (
        <Box display="flex" justifyContent="center" marginTop={3}>
          <Button
            variant="outlined"
            onClick={() => {
              Object.keys(audios).forEach((key) => {
                URL.revokeObjectURL(audios[key].url);
              });
              setAudios({});
            }}
            color="secondary"
          >
            Clear All Recordings
          </Button>
        </Box>
      )}
    </Card>
  );
});
